import { createApp, type App as AppInstance } from 'vue';
import { useRouter } from 'vue-router';
import { getAppLink } from '../request';
import { objectToUrlParams } from '../utils/url';
import { COMMUNITY_OPEN_APP_MESSAGE, h5OpenAppPage } from '../utils/redirect';
import { isInDocoiApp, isInWebCommunity } from '../utils/app';
import DownloadMask from '../components/DownloadMask.vue';
import { useAttribution } from '@h5-monorepo/attribution-sdk';
// 【废弃】openinstall 相关逻辑已整体替换为 @h5-monorepo/attribution-sdk，保留注释供历史参考
// import { useOpenInstall } from './useOpenInstall';
//
// // 站外场景预初始化 openinstall
// const openinstall = useOpenInstall();
// openinstall.init({
//   autoSchemeWakeup: false
// });

const attribution = useAttribution();
attribution.init();

/**
 * type 1 圈子邀请
 * type 2 打开动态，同时需要dynamicid
 * type 3 跳转对应圈子首页 cid = xxxx
 * type 5 打开H5，同时需要url、h5Type
 * type 6 打开unity场景
 * type 7 打开动态话题页面 topicId = xxx
 * type 8 打开首页并定位到对应圈子频道 channelId = xxx
 * type 10 打开攻略一级分类详情页面
 * type 11 打开用户主页,同时需要user_id
 * type 12 打开群聊页面,同时需要groupID、server_id
 * type 13 查询小群并跳转，同时需要inviteCode
 * type 14 打开圈子首页并跳转 serverId
 * type 15 打开金币充值页
 * type 16 打开我的-发布页面
 * type 17 打开游戏离线推送开关页 serverId
 * type 18 打开App并跳转到指定tab tab: 0首页 1发现页 2消息 3我的
 * type 25 打开私聊 userId: 用户id、 nickName用户昵称
 * 更多详情可查看飞书文档：https://dodjoyfs.feishu.cn/wiki/CRrtwvBNjihcpdkhnUTcp5lwnBh?from=from_copylink
 */
type Params = {
  type?: number | string;
  inviteCode?: string; //邀请码 type = 1
  url?: string; //打开的url
  cid?: string; // 圈子id
  h5Type?: number | string; //打开的H5的类型 1：普通的H5类型 2：小程序类型 3：动态链接 4：沉浸式
  urlType?: number; //8 为A5, 9 为T5 10 X5 当知道是哪个游戏时，用于下载固定渠道包
  dynamicid?: string; //动态id
  downloadUrl?: string; //渠道包下载地址
  user_id?: string; //用户id
  server_id?: string; //服务器id
  groupID?: string; //群聊id
  openInstallData?: Record<string, any>; //透传给 openinstall wakeupOrInstall 的自定义数据
  [key: string]: any;
};

export function useDownloadApp() {
  let maskAppInstance: AppInstance | null = null;
  let maskContainer: HTMLElement | null = null;
  let router: ReturnType<typeof useRouter> | null = null;
  try {
    router = useRouter();
  } catch {
    // 非组件上下文时 useRouter 会抛错，忽略即可
  }

  const showDownloadMask = (canHiden: boolean = true) => {
    // 如果已经存在遮罩，先移除
    hideDownloadMask();

    // 创建容器
    maskContainer = document.createElement('div');
    maskContainer.id = 'download-mask-container';
    document.body.appendChild(maskContainer);

    // 创建 Vue 应用实例
    maskAppInstance = createApp(DownloadMask, {
      canHiden,
      onClose: hideDownloadMask
    });

    // 挂载到容器
    maskAppInstance.mount(maskContainer);
  };

  const hideDownloadMask = () => {
    if (maskAppInstance && maskContainer) {
      maskAppInstance.unmount();
      document.body.removeChild(maskContainer);
      maskAppInstance = null;
      maskContainer = null;
    }
  };

  // type 不传时同样请求接口，只是不带 channel_type（拿官网默认包）
  const getDownloadUrl = (type?: number): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      getAppLink(type)
        .then(({ data: res }) => {
          if (res.data && res.data.package_url) {
            resolve(res.data.package_url);
          } else {
            resolve('https://cdn.docoi.cc/default/offical_pkg/production/mengyoushe.apk');
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const downloadApp = async (params?: Params) => {
    // 内嵌在 community 壳里：iframe 子文档拉不起 App，原样交给父级 community 执行
    if (isInWebCommunity()) {
      window.parent.postMessage({ type: COMMUNITY_OPEN_APP_MESSAGE, params }, '*');
      return;
    }
    const paramsStr = params ? '?' + objectToUrlParams(params) : '';
    const schemeUrl = `daoke://page${paramsStr}`;
    const u = navigator.userAgent;
    const isOpenHarmony = /OpenHarmony/i.test(u);
    const isWeixin = u.toLowerCase().includes('micromessenger');
    const isQQ = u.toLowerCase().includes('qq/');
    const isAndroid = u.includes('Android') || u.includes('Linux');
    const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);

    let isQQBrowser = u.toLowerCase().indexOf('qqbrowser') !== -1;
    let isQuark = u.toLowerCase().indexOf('quark') !== -1;
    let isUC = u.toLowerCase().indexOf('ucbrowser') !== -1;
    // 【废弃】以下归因数据仅服务于 openinstall.wakeupOrInstall，已替换为 attribution-sdk 的 report()
    // const { openInstallData, ...restParams } = params || {};
    // const userSource = router?.currentRoute?.value?.meta?.userSource as string | undefined;
    // const downloadData = {
    //   ...restParams,
    //   ...(userSource && { userSource }),
    //   ...openInstallData
    // };
    // 自动从当前路由 meta 中读取 userSource 归因参数，此刻路由已确定，比预热时更准确
    const userSource = router?.currentRoute?.value?.meta?.userSource as string | undefined;
    try {
      if (isInDocoiApp()) {
        // 梦游社内，鸿蒙与其他行为一致
        if (isAndroid) window.location.href = schemeUrl;
        else if (isIOS) h5OpenAppPage(schemeUrl as `daoke://page${string}`);
      } else {
        attribution.report({ userSource });
        // 非 App 内场景统一走原鸿蒙逻辑（原仅鸿蒙走，现全渠道通用）：有传入 downloadUrl 直接用，否则请求接口获取
        if (isWeixin || isQQ) {
          showDownloadMask(true);
          window.location.href = schemeUrl;
        } else if (isIOS) {
          //ios的scheme协议
          if (isQuark || isQQBrowser || isUC) {
            window.location.href = `daoke://page${paramsStr}`; //已安装时各浏览器都能正常打开，未安装时不能跳转app store；
            setTimeout(function () {
              let hidden = window.document.hidden;
              if (typeof hidden == 'undefined' || hidden == false) {
                window.location.href = `https://apps.apple.com/cn/app/%E9%81%93%E5%8F%AF/id1587114522`; //直接打开app store
                // window.location.href = 'https://mys.dodjoy.com/';
              }
            }, 2000);
          } else {
            window.location.href = `https://app.docoi.cc/open-app${paramsStr}`; //已安装时safari,chrome可以打开，未安装时只有夸克不能打开app store
            // window.location.href = `daoke://page${paramsStr}`;
          }
        } else {
          window.location.href = `daoke://page${paramsStr}`;
          let apkUrl = params?.downloadUrl;
          if (!apkUrl) {
            try {
              // 鸿蒙取最新渠道包传 23，其他渠道不传参数
              apkUrl = await getDownloadUrl(isOpenHarmony ? 23 : undefined);
            } catch {
              apkUrl = 'https://cdn.docoi.cc/default/offical_pkg/production/mengyoushe.apk';
            }
          }
          setTimeout(function () {
            let hidden = window.document.hidden;
            if (typeof hidden == 'undefined' || hidden == false) {
              window.location.href = apkUrl!;
            }
          }, 1500);
        }
      }
      // 【废弃】原走 openinstall.wakeupOrInstall，已替换为上方的 reportAttribution()
      // openinstall.wakeupOrInstall({ data: downloadData });
    } catch (err) {
      console.log('err', err);
    }
  };

  return {
    downloadApp,
    showDownloadMask,
    hideDownloadMask,
    getDownloadUrl
  };
}

// 导出单例实例，确保全局唯一
export const { downloadApp, showDownloadMask, hideDownloadMask, getDownloadUrl } = useDownloadApp();
